import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

const NOTES_API = '/api/v1/sticky-notes';

// ─── Types ───────────────────────────────────────────────────────────────────

export type StickyNoteColor = 'yellow' | 'blue' | 'green' | 'pink' | 'orange' | 'purple';

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface StickyNote {
  id: number;
  organization: number;
  project: number | null;
  location: number | null;
  content: string;
  color: StickyNoteColor;
  position_x: number;
  position_y: number;
  is_deleted: boolean;
  created_by: number | null;
  created_by_username: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateStickyNotePayload {
  content: string;
  color?: StickyNoteColor;
  position_x?: number;
  position_y?: number;
  project?: number | null;
  location?: number | null;
}

export interface UpdateStickyNotePayload {
  id: number;
  content?: string;
  color?: StickyNoteColor;
  position_x?: number;
  position_y?: number;
  project?: number | null;
  location?: number | null;
}

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useStickyNotes() {
  return useQuery<StickyNote[]>({
    queryKey: queryKeys.staffingBoard.notes(),
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<StickyNote>>(`${NOTES_API}/`);
      return response.data.results ?? [];
    },
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateStickyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateStickyNotePayload) => {
      const response = await apiClient.post<StickyNote>(`${NOTES_API}/`, payload);
      return response.data;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: queryKeys.staffingBoard.notes() });
      const previous = qc.getQueryData<StickyNote[]>(queryKeys.staffingBoard.notes());
      if (previous) {
        const optimistic: StickyNote = {
          id: -Date.now(),
          organization: 0,
          project: payload.project ?? null,
          location: payload.location ?? null,
          content: payload.content,
          color: payload.color ?? 'yellow',
          position_x: payload.position_x ?? 0,
          position_y: payload.position_y ?? 0,
          is_deleted: false,
          created_by: null,
          created_by_username: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        qc.setQueryData<StickyNote[]>(queryKeys.staffingBoard.notes(), [...previous, optimistic]);
      }
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKeys.staffingBoard.notes(), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.notes() });
    },
  });
}

export function useUpdateStickyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateStickyNotePayload) => {
      const response = await apiClient.patch<StickyNote>(`${NOTES_API}/${id}/`, payload);
      return response.data;
    },
    onMutate: async ({ id, ...payload }) => {
      await qc.cancelQueries({ queryKey: queryKeys.staffingBoard.notes() });
      const previous = qc.getQueryData<StickyNote[]>(queryKeys.staffingBoard.notes());
      if (previous) {
        qc.setQueryData<StickyNote[]>(
          queryKeys.staffingBoard.notes(),
          previous.map((note) =>
            note.id === id ? { ...note, ...payload, updated_at: new Date().toISOString() } : note,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKeys.staffingBoard.notes(), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.notes() });
    },
  });
}

export function useDeleteStickyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`${NOTES_API}/${id}/`);
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.staffingBoard.notes() });
      const previous = qc.getQueryData<StickyNote[]>(queryKeys.staffingBoard.notes());
      if (previous) {
        qc.setQueryData<StickyNote[]>(
          queryKeys.staffingBoard.notes(),
          previous.filter((note) => note.id !== id),
        );
      }
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKeys.staffingBoard.notes(), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.notes() });
    },
  });
}
